import React, { useState, useEffect } from 'react';
import { PageLayout } from '../../components/organisms/PageLayout';
import ConfirmDeleteModal from '../../components/organisms/ConfirmDeleteModal';
import FilterChips, { ChipItem, ChipKey } from '../../components/molecules/documents/FilterChips';
import DocumentTile, { DocType } from '../../components/molecules/documents/DocumentTile';
import DocumentsTable, { TableDoc } from '../../components/molecules/documents/DocumentsTable';
import EditorModal from '../../components/molecules/documents/EditorModal';
import TextDocumentModal, { TextDocData } from '../../components/molecules/documents/modals/TextDocumentModal';
import NewTextDocumentModal from '../../components/molecules/documents/modals/new/options/NewTextDocumentModal';
import PDFDocumentModal, { PdfDocData } from '../../components/molecules/documents/modals/PDFDocumentModal';
import NewPDFDocumentModal, { PdfDocData as NewPdfDocData } from '../../components/molecules/documents/modals/new/options/NewPDFDocumentModal';
import NewDocumentModal from '../../components/molecules/documents/modals/new/NewDocumentModal';
import api, { getUserUid } from '../../utils/api';
import { API_ENDPOINTS, S3_BASE_URL } from '../../config';
import { emitSubscriptionRefresh } from '../../utils/subscriptionEvents';
import { showToast } from '../../utils/toastManager';

interface DocumentFromAPI {
  uid: string;
  user_uid: string;
  kanban_uid?: string;
  job_uid?: string;
  type: 'text' | 'doc' | 'ai';
  url?: string;
  name: string;
  doc_category: string;
  description?: string;
  text_value?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

const Documents: React.FC = () => {
  const breadcrumbItems = [
    { label: "Home", href: "/" },
    { label: "Documents" }
  ];

  useEffect(() => {
    document.title = 'OfferFlow | Documents';
  }, []);

  const [view, setView] = useState<'list' | 'grid'>('grid');
  const [activeFilter, setActiveFilter] = useState<ChipKey>('all');
  const [editorId, setEditorId] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    documentId: string | null;
    documentName: string;
  }>({
    isOpen: false,
    documentId: null,
    documentName: '',
  });
  const [newDocModalOpen, setNewDocModalOpen] = useState(false);
  const [newTextDocModalOpen, setNewTextDocModalOpen] = useState(false);
  const [newPdfDocModalOpen, setNewPdfDocModalOpen] = useState(false);
  const [textDocModalOpen, setTextDocModalOpen] = useState(false);
  const [editingTextDocId, setEditingTextDocId] = useState<string | null>(null);
  const [currentTextDoc, setCurrentTextDoc] = useState<TextDocData>({
    title: '',
    category: 'Resume',
    description: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    content: '',
  });
  const [pdfDocModalOpen, setPdfDocModalOpen] = useState(false);
  const [editingPdfDocId, setEditingPdfDocId] = useState<string | null>(null);
  const [currentPdfDoc, setCurrentPdfDoc] = useState<PdfDocData>({
    title: '',
    category: 'Resume',
    description: '',
  });
  const [currentPdfUrl, setCurrentPdfUrl] = useState<string | undefined>(undefined);
  
  const [documents, setDocuments] = useState<TableDoc[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(true);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 14) return '1 week ago';
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const transformDocument = (apiDoc: DocumentFromAPI): TableDoc => {
    const extension = (apiDoc.type === 'text' || apiDoc.type === 'ai') ? '.txt' : '.pdf';
    const fileName = apiDoc.name.endsWith(extension) ? apiDoc.name : `${apiDoc.name}${extension}`;
    
    return {
      id: apiDoc.uid,
      name: fileName,
      type: apiDoc.doc_category,
      updated: formatDate(apiDoc.created_at),
      documentType: apiDoc.type,
      _raw: apiDoc,
    };
  };

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoadingDocuments(true);
        const data = await api.post<DocumentFromAPI[]>(API_ENDPOINTS.documents.list);
        
        if (Array.isArray(data)) {
          const transformedDocs = data.map(transformDocument);
          setDocuments(transformedDocs);
        } else {

          setDocuments([]);
        }
      } catch (error) {

        setDocuments([]);
      } finally {
        setLoadingDocuments(false);
      }
    };

    fetchDocuments();
  }, []);

  const chips: ChipItem[] = React.useMemo(() => {
    const categoryCounts = documents.reduce((acc, doc) => {
      const category = doc.type;
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const result: ChipItem[] = [
      { key: 'all', label: 'All', count: documents.length }
    ];

    Object.entries(categoryCounts)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([category, count]) => {
        result.push({
          key: category.toLowerCase().replace(/\s+/g, '_'),
          label: category,
          count: count
        });
      });

    return result;
  }, [documents]);

  const filteredDocuments = activeFilter === 'all'
    ? documents
    : documents.filter(d => {
        const categoryKey = d.type.toLowerCase().replace(/\s+/g, '_');
        return categoryKey === activeFilter;
      });

  const handleNewDocumentClick = () => {
    setNewDocModalOpen(true);
  };

  const handleToggleView = () => {
    setView(v => v === 'list' ? 'grid' : 'list');
  };

  const handleOpenDocument = (id: string) => {
    const doc = documents.find(d => d.id === id);
    if (!doc || !doc._raw) return;

    const rawDoc = doc._raw as DocumentFromAPI;

    const isTextDoc = rawDoc.type === 'text' || rawDoc.type === 'ai';
    const isPdfDoc = rawDoc.type === 'doc';
    
    if (isTextDoc) {
      setEditingTextDocId(id);
      setCurrentTextDoc({
        title: rawDoc.name,
        category: rawDoc.doc_category,
        description: rawDoc.description || '',
        firstName: rawDoc.first_name || '',
        lastName: rawDoc.last_name || '',
        email: rawDoc.email || '',
        phone: rawDoc.phone_number || '',
        content: rawDoc.text_value || '',
        boardLinked: rawDoc.kanban_uid,
        jobLinked: rawDoc.job_uid,
      });
      setTextDocModalOpen(true);
    } else if (isPdfDoc) {
      if (!rawDoc.url) {

      }
      
      setEditingPdfDocId(id);
      setCurrentPdfDoc({
        title: rawDoc.name,
        category: rawDoc.doc_category,
        description: rawDoc.description || '',
        boardLinked: rawDoc.kanban_uid,
        jobLinked: rawDoc.job_uid,
      });
      
      const documentUrl = rawDoc.url && rawDoc.url.trim()
        ? `${S3_BASE_URL}${rawDoc.url.trim()}`
        : undefined;
      setCurrentPdfUrl(documentUrl);
      setPdfDocModalOpen(true);
    } else {
      setEditorId(id);
    }
  };

  const handleEditDocument = (id: string) => {
    handleOpenDocument(id);
  };

  const handleDeleteClick = (id: string) => {
    const doc = documents.find(d => d.id === id);
    if (doc) {
      setDeleteModal({
        isOpen: true,
        documentId: id,
        documentName: doc.name,
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteModal({
      isOpen: false,
      documentId: null,
      documentName: '',
    });
  };

  const handleConfirmDelete = async () => {
    if (deleteModal.documentId === null) return;

    try {
      await api.delete(API_ENDPOINTS.documents.delete, { document_uid: deleteModal.documentId });
      
      setDocuments(documents.filter((doc) => doc.id !== deleteModal.documentId));
      
      emitSubscriptionRefresh();
      
      setDeleteModal({
        isOpen: false,
        documentId: null,
        documentName: '',
      });
    } catch (error: any) {


      if (error?.status === 404) {
        setDocuments(documents.filter((doc) => doc.id !== deleteModal.documentId));
        setDeleteModal({
          isOpen: false,
          documentId: null,
          documentName: '',
        });
      } else if (error?.status === 401) {

      } else {

      }
    }
  };

  const handleTextDocCommit = async (data: TextDocData) => {
    if (!editingTextDocId) {

      return;
    }

    try {
      if (!data.title?.trim()) {

        return;
      }

      const documentPayload = {
        document_uid: editingTextDocId,
        type: 'text' as const,
        name: data.title.trim(),
        doc_category: data.category || 'Other',
        description: data.description?.trim() || null,
        text_value: data.content?.trim() || null,
        first_name: data.firstName?.trim() || null,
        last_name: data.lastName?.trim() || null,
        email: data.email?.trim() || null,
        phone_number: data.phone?.trim() || null,
        kanban_uid: data.boardLinked || null,
        job_uid: data.jobLinked || null,
      };

      await api.put(API_ENDPOINTS.documents.update, documentPayload);
      
      setTextDocModalOpen(false);
      setEditingTextDocId(null);
      
      const updatedDocs = await api.post<DocumentFromAPI[]>(API_ENDPOINTS.documents.list);
      if (Array.isArray(updatedDocs)) {
        const transformedDocs = updatedDocs.map(transformDocument);
        setDocuments(transformedDocs);
      }
      
      showToast('Document updated successfully', 'success');
    } catch (error: any) {
      console.error('Update document error:', error);
      
      if (error?.status === 422) {
        showToast('Validation error. Please check your input.', 'error');
      } else if (error?.status === 401) {
        showToast('Unauthorized. Please log in again.', 'error');
      } else if (error?.status === 404) {
        showToast('Document not found. It may have been deleted.', 'error');
      } else {
        showToast('Failed to update document. Please try again.', 'error');
      }
    }
  };

  const handlePdfDocCommit = async (data: PdfDocData) => {
    if (!editingPdfDocId) {

      return;
    }

    try {
      if (!data.title?.trim()) {

        return;
      }

      const documentPayload: any = {
        document_uid: editingPdfDocId,
        type: 'doc' as const,
        name: data.title.trim(),
        doc_category: data.category || 'Other',
      };

      if (data.description?.trim()) {
        documentPayload.description = data.description.trim();
      }

      if (data.boardLinked) {
        documentPayload.kanban_uid = data.boardLinked;
      } else {
        documentPayload.kanban_uid = null;
      }

      if (data.jobLinked) {
        documentPayload.job_uid = data.jobLinked;
      } else {
        documentPayload.job_uid = null;
      }

      await api.put(API_ENDPOINTS.documents.update, documentPayload);
      
      setPdfDocModalOpen(false);
      setEditingPdfDocId(null);
      
      const updatedDocs = await api.post<DocumentFromAPI[]>(API_ENDPOINTS.documents.list);
      if (Array.isArray(updatedDocs)) {
        const transformedDocs = updatedDocs.map(transformDocument);
        setDocuments(transformedDocs);
      }
    } catch (error: any) {

      
      if (error?.status === 422) {

      } else if (error?.status === 401) {

      } else if (error?.status === 404) {

      } else {

      }
    }
  };

  const handleNewTextDocCommit = async (data: TextDocData) => {
    try {
      if (!data.title?.trim()) {
        showToast('Enter a title for the document', 'error');
        return;
      }

      const user_uid = getUserUid();
      if (!user_uid) {
        showToast('Your session expired. Please sign in again.', 'error');
        return;
      }

      const documentPayload = {
        user_uid,
        type: 'text' as const,
        name: data.title.trim(),
        doc_category: data.category || 'Other',
        description: data.description?.trim() || null,
        text_value: data.content?.trim() || null,
        first_name: data.firstName?.trim() || null,
        last_name: data.lastName?.trim() || null,
        email: data.email?.trim() || null,
        phone_number: data.phone?.trim() || null,
        kanban_uid: data.boardLinked || null,
        job_uid: data.jobLinked || null,
      };

      await api.post(API_ENDPOINTS.documents.create, documentPayload);
      
      setNewTextDocModalOpen(false);
      
      const updatedDocs = await api.post<DocumentFromAPI[]>(API_ENDPOINTS.documents.list);
      if (Array.isArray(updatedDocs)) {
        const transformedDocs = updatedDocs.map(transformDocument);
        setDocuments(transformedDocs);
      }
      
      emitSubscriptionRefresh();
    } catch (error: any) {
      if (error?.status === 401) {
        showToast('Your session expired. Please sign in again.', 'error');
      } else {
        showToast('Failed to create the document. Please try again.', 'error');
      }
    }
  };

  const handleNewPdfDocCommit = async (data: NewPdfDocData) => {
    try {
      if (!data.file) {
        return;
      }
      if (!data.title?.trim()) {
        return;
      }

      const formData = new FormData();
      formData.append('type', 'doc');
      formData.append('name', data.title.trim());
      formData.append('doc_category', data.category || 'Other');
      
      if (data.description?.trim()) {
        formData.append('description', data.description.trim());
      }
      
      if (data.boardLinked) {
        formData.append('kanban_uid', data.boardLinked);
      }
      
      if (data.jobLinked) {
        formData.append('job_uid', data.jobLinked);
      }
      
      formData.append('file', data.file);

      await api.postFormData(API_ENDPOINTS.documents.upload, formData);
      
      setNewPdfDocModalOpen(false);
      
      const updatedDocs = await api.post<DocumentFromAPI[]>(API_ENDPOINTS.documents.list);
      if (Array.isArray(updatedDocs)) {
        const transformedDocs = updatedDocs.map(transformDocument);
        setDocuments(transformedDocs);
      }
    } catch (error: any) {

      
      if (error?.status === 422) {

      } else if (error?.status === 401) {

      } else {

      }
    }
  };

  return (
    <PageLayout 
      breadcrumbItems={breadcrumbItems}
      actions={
        <button className="breadcrumbs__action-btn" onClick={handleNewDocumentClick}>
          + New document
        </button>
      }
    >
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        <FilterChips
          chips={chips}
          activeKey={activeFilter}
          onChange={setActiveFilter}
          view={view}
          onToggleView={handleToggleView}
        />

        <div style={{ borderBottom: '1px solid #e5e7eb', margin: '16px 0' }} />

        {loadingDocuments ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280', fontSize: '14px' }}>
            Loading documents...
          </div>
        ) : view === 'grid' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
            {filteredDocuments.map(d => (
              <DocumentTile
                key={d.id}
                id={d.id}
                name={d.name}
                type={d.type as DocType}
                updated={d.updated}
                documentType={d.documentType}
                onOpen={handleOpenDocument}
                onEdit={handleEditDocument}
                onDelete={handleDeleteClick}
              />
            ))}
          </div>
        ) : (
          <DocumentsTable
            rows={filteredDocuments}
            onOpen={handleOpenDocument}
            onEdit={handleEditDocument}
            onDelete={handleDeleteClick}
          />
        )}

        {!loadingDocuments && filteredDocuments.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280', fontSize: '14px' }}>
            No documents in this filter.
          </div>
        )}
      </div>

      <NewDocumentModal
        open={newDocModalOpen}
        onClose={() => setNewDocModalOpen(false)}
        title="Create New Document"
        size="md"
      >
        <p style={{ margin: '0 0 12px 0', color: '#6b7280', fontSize: '14px', textAlign: 'left' }}>
          Choose the type of document you want to create:
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button
            className="modal__btn"
            style={{ textAlign: 'left', padding: '12px 16px', height: 'auto' }}
            onClick={() => {
              setNewDocModalOpen(false);
              setNewTextDocModalOpen(true);
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Create Text Document</div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Create a new text document using the inline text editor.</div>
          </button>
          <button
            className="modal__btn"
            style={{ textAlign: 'left', padding: '12px 16px', height: 'auto' }}
            onClick={() => {
              setNewDocModalOpen(false);
              setNewPdfDocModalOpen(true);
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Upload File</div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Upload an existing file from your computer. Allowed file types: .pdf, .doc, .docx, .png, .jpg</div>
          </button>
        </div>
      </NewDocumentModal>

      <NewTextDocumentModal
        open={newTextDocModalOpen}
        savedValue={{
          title: '',
          category: 'Resume',
          description: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          content: '',
        }}
        onClose={() => setNewTextDocModalOpen(false)}
        onCommit={handleNewTextDocCommit}
      />

      <NewPDFDocumentModal
        open={newPdfDocModalOpen}
        savedValue={{
          title: '',
          category: 'Resume',
          description: '',
        }}
        onClose={() => setNewPdfDocModalOpen(false)}
        onCommit={handleNewPdfDocCommit}
      />

      <TextDocumentModal
        open={textDocModalOpen}
        savedValue={currentTextDoc}
        onClose={() => {
          setTextDocModalOpen(false);
          setEditingTextDocId(null);
        }}
        onCommit={handleTextDocCommit}
      />

      <PDFDocumentModal
        open={pdfDocModalOpen}
        savedValue={currentPdfDoc}
        onClose={() => {
          setPdfDocModalOpen(false);
          setEditingPdfDocId(null);
        }}
        onCommit={handlePdfDocCommit}
        pdfUrl={currentPdfUrl}
      />

      <EditorModal 
        open={!!editorId} 
        title="View document"
        onClose={() => setEditorId(null)} 
      >
        <div style={{ color: '#6b7280', fontSize: '14px', textAlign: 'center', padding: '40px' }}>
          Other file types viewer will be implemented here.
        </div>
      </EditorModal>

      <ConfirmDeleteModal
        isOpen={deleteModal.isOpen}
        itemName={deleteModal.documentName}
        itemType="Document"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </PageLayout>
  );
};

export default Documents;
